import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Sparkles, Search, X, Users, Check, AlertCircle, Wand2, Image as ImageIcon } from 'lucide-react';
import { getUsers, createRecognition, enhanceMessage } from '../services/api';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Grid } from '@giphy/react-components';

const gf = new GiphyFetch('ihUkZni3rNCH5kEJv5eZpDhe9yJTtMnF');
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';

const CORE_VALUES = [
  { id: 'collaborative', name: 'Collaborative', nameChinese: '合作', code: 'C' },
  { id: 'dedicated', name: 'Dedicated', nameChinese: '敬业', code: 'D' },
  { id: 'entrepreneurial', name: 'Entrepreneurial', nameChinese: '创业精神', code: 'E' },
  { id: 'fun', name: 'Fun', nameChinese: '乐趣', code: 'F' },
];

const FUNDAMENTALS = [
  { id: 1, name: 'Do the right thing, always', nameChinese: '始终，为所应为' },
  { id: 2, name: 'Be a fanatic about response time', nameChinese: '做一名响应迅速的狂热分子' },
  { id: 3, name: 'Demonstrate a design mentality', nameChinese: '体现出设计思维' },
  { id: 4, name: 'Think globally', nameChinese: '具备国际思维' },
  { id: 5, name: 'Be proud of your work', nameChinese: '为自己的工作感到自豪' },
  { id: 6, name: 'Honor commitments', nameChinese: '信守承诺' },
  { id: 7, name: 'Practice blameless problem-solving', nameChinese: '解决问题，不指责' },
  { id: 8, name: 'Make a difference', nameChinese: '做出贡献' },
  { id: 9, name: 'Get clear on expectations', nameChinese: '明确要求' },
  { id: 10, name: 'Listen generously', nameChinese: '认真倾听' },
  { id: 11, name: 'Speak straight', nameChinese: '坦诚直言' },
  { id: 12, name: 'Think team first', nameChinese: '优先思考团队' },
  { id: 13, name: 'Compete to win', nameChinese: '竞争制胜' },
  { id: 14, name: 'Take ownership', nameChinese: '发挥主人翁精神' },
  { id: 15, name: 'Be process-driven', nameChinese: '以流程为导向' },
  { id: 16, name: 'Assume positive intent', nameChinese: '用善意的眼光看待他人' },
  { id: 17, name: 'Be relentless about improvement', nameChinese: '坚持不懈地追求进步' },
  { id: 18, name: 'Show meaningful appreciation', nameChinese: '注重真诚的表扬' },
  { id: 19, name: 'Think and act like an owner', nameChinese: '倡导主人翁思维和行为' },
  { id: 20, name: 'Be positive', nameChinese: '正向鼓励' },
  { id: 21, name: 'Collaborate', nameChinese: '合作' },
  { id: 22, name: 'Pay attention to the details', nameChinese: '注重细节' },
  { id: 23, name: 'Be inclusive', nameChinese: '心存包容' },
  { id: 24, name: 'Be a brand ambassador', nameChinese: '成为品牌大使' },
  { id: 25, name: 'Own your work-life balance', nameChinese: '平衡工作和生活' },
  { id: 26, name: 'Embrace change', nameChinese: '欢迎变革' },
  { id: 27, name: "Walk in your customers' shoes", nameChinese: '想客户所想' },
  { id: 28, name: 'Share information', nameChinese: '分享信息' },
  { id: 29, name: 'Look ahead and anticipate', nameChinese: '未雨绸缪' },
  { id: 30, name: 'Keep things fun', nameChinese: '保持乐趣' },
];

const MIN_LENGTH_ENGLISH = 140;
const MIN_LENGTH_CHINESE = 50;

// TEMPORARY (testing): let this one user pick themselves as a recipient so Jared
// can test self-recognition. Remove when testing is done (also in the backend).
const SELF_RECOGNITION_TEST_USER_ID = '240e8f23-117b-4876-a37d-300f10233dc0';

// Detect if text is primarily Chinese (>30% Chinese characters)
function isChinese(text) {
  const chineseChars = (text.match(/[\u4e00-\u9fff]/g) || []).length;
  return chineseChars > text.length * 0.3;
}

function getMinLength(text, selectedLanguage) {
  // If user has typed content, detect language from text
  if (text && text.length > 0) {
    return isChinese(text) ? MIN_LENGTH_CHINESE : MIN_LENGTH_ENGLISH;
  }
  // Otherwise default based on selected program language
  return selectedLanguage === 'zh' ? MIN_LENGTH_CHINESE : MIN_LENGTH_ENGLISH;
}

export function RecognitionModal({ trigger, currentUser, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedCoreValue, setSelectedCoreValue] = useState('');
  const [selectedFundamental, setSelectedFundamental] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [preEnhanceMessage, setPreEnhanceMessage] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [gifSearch, setGifSearch] = useState('');
  const { language } = useLanguage();
  const timeoutRef = useRef(null);

  const [users, setUsers] = useState([]);

  const handleDismiss = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowConfetti(false);
    setOpen(false);
    setSelectedRecipients([]);
    setSelectedCoreValue('');
    setSelectedFundamental('');
    setMessage('');
    setWarning(null);
    setSelectedGif(null);
    setShowGifPicker(false);
    setGifSearch('');
    setPreEnhanceMessage(null);
    setShowOriginal(false);
    if (onSuccess) onSuccess();
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  async function loadData() {
    try {
      const usersRes = await getUsers();
      const allowSelf = currentUser?.id === SELF_RECOGNITION_TEST_USER_ID;
      setUsers(usersRes.data.filter(u => allowSelf || u.id !== currentUser?.id));
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  }

  const selectedIds = new Set(selectedRecipients.map(r => r.id));
  const filteredUsers = users.filter(
    user =>
      !selectedIds.has(user.id) &&
      searchQuery &&
      (user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.department.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSelectRecipient = (user) => {
    if (selectedRecipients.length >= 10) return;
    setSelectedRecipients([...selectedRecipients, user]);
    setSearchQuery('');
  };

  const handleRemoveRecipient = (userId) => {
    setSelectedRecipients(selectedRecipients.filter(r => r.id !== userId));
  };

  const handleSubmit = async () => {
    const minLength = getMinLength(message.trim(), language);
    if (selectedRecipients.length === 0 || !selectedCoreValue || !selectedFundamental || message.trim().length < minLength) {
      return;
    }

    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      await createRecognition({
        receiver_ids: selectedRecipients.map(r => r.id),
        message: message.trim(),
        core_value: selectedCoreValue || null,
        fundamental_id: selectedFundamental ? parseInt(selectedFundamental) : null,
        gif_url: selectedGif || null
      });

      setShowConfetti(true);
      timeoutRef.current = setTimeout(() => {
        handleDismiss();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnhance = async () => {
    if (!message.trim()) return;
    const original = message.trim();
    setEnhancing(true);
    setError(null);
    try {
      const valueName = CORE_VALUES.find(v => v.id === selectedCoreValue)?.name || '';
      const fundName = FUNDAMENTALS.find(f => String(f.id) === selectedFundamental)?.name || '';
      const recipientName = selectedRecipients.map(r => r.name).join(', ');
      const res = await enhanceMessage(original, recipientName, valueName, fundName);
      if (res.data?.enhanced) {
        setPreEnhanceMessage(original);
        setShowOriginal(false);
        setMessage(res.data.enhanced);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setEnhancing(false);
    }
  };

  const handleRevertEnhance = () => {
    if (preEnhanceMessage !== null) {
      setMessage(preEnhanceMessage);
      setPreEnhanceMessage(null);
      setShowOriginal(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const minMessageLength = getMinLength(message.trim(), language);
  const isValid = selectedRecipients.length > 0 && selectedCoreValue && selectedFundamental && message.trim().length >= minMessageLength;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="gap-2 shadow-lg hover:shadow-xl transition-shadow">
            <Sparkles className="h-4 w-4" />
            {t('giveRecognition', language)}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {showConfetti && (
          <div className="absolute inset-0 overflow-hidden rounded-lg z-50">
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-bounce"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    animationDuration: `${0.5 + Math.random() * 0.5}s`,
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: ['#E31937', '#FFD700', '#4CAF50', '#2196F3', '#FF9800'][
                        Math.floor(Math.random() * 5)
                      ],
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white/80">
              <div className="text-center px-4">
                <Check className="h-16 w-16 text-green-500 mx-auto mb-2 animate-pulse" />
                <p className="text-lg font-semibold text-foreground">{t('recognitionSent', language)}</p>
              </div>
            </div>
          </div>
        )}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('newRecognition', language)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {/* Recipient Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('whoRecognize', language)}
            </Label>

            {/* Selected Recipients */}
            {selectedRecipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedRecipients.map(recipient => (
                  <Badge
                    key={recipient.id}
                    variant="secondary"
                    className="pl-1 pr-1 py-1 flex items-center gap-1"
                  >
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-xs">
                        {getInitials(recipient.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs">{recipient.name}</span>
                    <button
                      onClick={() => handleRemoveRecipient(recipient.id)}
                      className="ml-1 hover:bg-muted rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <span className="text-xs text-muted-foreground self-center">
                  {selectedRecipients.length}/10
                </span>
              </div>
            )}

            {/* Search Input */}
            {selectedRecipients.length < 10 && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('searchColleagues', language)}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* User Results */}
                {searchQuery && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-3 text-center">
                        No users found
                      </p>
                    ) : (
                      filteredUsers.slice(0, 5).map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleSelectRecipient(user)}
                          className="w-full flex items-center gap-3 p-2 hover:bg-muted transition-colors text-left"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {user.department} • {user.location}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Core Value Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {language === 'zh' ? '核心价值观' : 'Core Value'}
            </Label>
            <Select value={selectedCoreValue} onValueChange={setSelectedCoreValue}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'zh' ? '选择核心价值观...' : 'Select a core value...'} />
              </SelectTrigger>
              <SelectContent>
                {CORE_VALUES.map(value => (
                  <SelectItem key={value.id} value={value.id}>
                    {value.code} – {language === 'zh' ? value.nameChinese : value.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fundamental Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {language === 'zh' ? '基本原则' : 'Fundamental'}
            </Label>
            <Select value={selectedFundamental} onValueChange={setSelectedFundamental}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'zh' ? '选择基本原则...' : 'Select a fundamental...'} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {FUNDAMENTALS.map(f => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    #{f.id} – {language === 'zh' ? f.nameChinese : f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('writeMessage', language)}</Label>
            <Textarea
              placeholder={t('messagePrompt', language)}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleEnhance}
                  disabled={!message.trim() || enhancing}
                  className="gap-1.5 text-xs"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {enhancing
                    ? (language === 'zh' ? 'AI 优化中...' : 'Enhancing...')
                    : (language === 'zh' ? 'AI 优化' : 'Make Better with AI')}
                </Button>
                {!selectedGif && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowGifPicker(!showGifPicker)}
                    className="gap-1.5 text-xs"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    {showGifPicker
                      ? (language === 'zh' ? '关闭' : 'Close')
                      : (language === 'zh' ? '添加贴纸' : 'Add Sticker')}
                  </Button>
                )}
              </div>
              <p className={`text-xs ${message.length < minMessageLength ? 'text-destructive' : 'text-muted-foreground'}`}>
                {message.length}/{minMessageLength}
              </p>
            </div>

            {/* AI enhance: compare with / revert to the original */}
            {preEnhanceMessage !== null && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1 text-xs font-medium text-primary">
                    <Wand2 className="h-3.5 w-3.5" />
                    {language === 'zh' ? 'AI 已优化你的留言' : 'AI enhanced your message'}
                  </span>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowOriginal(!showOriginal)}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      {showOriginal
                        ? (language === 'zh' ? '隐藏原文' : 'Hide original')
                        : (language === 'zh' ? '查看原文' : 'View original')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRevertEnhance}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      {language === 'zh' ? '还原' : 'Revert'}
                    </button>
                  </div>
                </div>
                {showOriginal && (
                  <div className="rounded border bg-background p-2">
                    <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {language === 'zh' ? '原文' : 'Original'}
                    </p>
                    <p className="whitespace-pre-wrap text-sm text-foreground">{preEnhanceMessage}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* GIF/Sticker */}
          <div className="space-y-2">
            {selectedGif && (
              <div className="relative inline-block">
                <img src={selectedGif} alt="Selected GIF" className="rounded-lg max-h-32" />
                <button
                  onClick={() => setSelectedGif(null)}
                  className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {showGifPicker && !selectedGif && (
              <div className="border rounded-lg p-2 space-y-2">
                <Input
                  placeholder={language === 'zh' ? '搜索贴纸...' : 'Search stickers...'}
                  value={gifSearch}
                  onChange={(e) => setGifSearch(e.target.value)}
                  className="text-sm"
                />
                <div className="h-64 overflow-y-auto rounded">
                  <Grid
                    key={gifSearch}
                    width={440}
                    columns={3}
                    gutter={6}
                    fetchGifs={(offset) =>
                      gifSearch
                        ? gf.search(gifSearch, { offset, limit: 12 })
                        : gf.trending({ offset, limit: 12 })
                    }
                    onGifClick={(gif, e) => {
                      e.preventDefault();
                      setSelectedGif(gif.images.fixed_height.url);
                      setShowGifPicker(false);
                    }}
                    noLink
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!isValid || loading}
            className="w-full gap-2"
            size="lg"
          >
            <Sparkles className="h-4 w-4" />
            {loading ? t('submitting', language) : t('submit', language)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RecognitionModal;
